import { Box, SxProps, Skeleton } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDevicePixelRatio } from '../lib/hooks/useDevicePixelRatio'
import { baseGetImageKitUrl } from '../../shared-solo/lib/imageUtils'

interface ImageProps {
  src: string
  width?: number
  height?: number
  sx?: SxProps
  onClick?: () => void
  loadedComponent?: React.ReactNode
}

const imageKitUrl = import.meta.env.VITE_IMAGE_KIT_URL
const imagesLocation = import.meta.env.VITE_IMAGES_LOCATION

type ImageKitParams = {
  width?: number
  height?: number
  devicePixelRatio?: number
}

export function getImageKitUrl(src: string, params?: ImageKitParams) {
  const url = baseGetImageKitUrl({ src, imageKitUrl, imagesLocation })
  if (!url || !params) return url
  return addImageKitParams(url, params)
}

const addImageKitParams = (src: string, params: ImageKitParams) => {
  if (!src) return ''
  const { width, height, devicePixelRatio = 1 } = params

  const url = new URL(src)
  const w = width ? width * devicePixelRatio : 'auto'
  const h = height ? height * devicePixelRatio : 'auto'
  url.searchParams.set('tr', `w-${w},h-${h}`)
  return url.toString()
}

const ImageView = (props: ImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const { src, width, height, sx, ...rest } = props
  const [imageSrc, setImageSrc] = useState<string>('')
  const devicePixelRatio = useDevicePixelRatio()

  const isImageKit = src?.startsWith(imageKitUrl)

  if (isImageKit && (!props.width && !props.height)) {
    console.error('ImageKit image URL provided without transformation parameters')
  }

  if (!isImageKit && (props.width || props.height)) {
    console.error('Non ImageKit URL provided with transformation parameters')
  }

  const dimensions = {
    width: width || '100%',
    height: height || '100%',
  }

  const skeletonDimensions = {
    ...dimensions,
    minWidth: width || '100%',
    minHeight: height || '100%',
  }

  useEffect(() => {
    const image = new Image()
    image.src = imageSrc
    image.onload = () => setIsLoaded(true)
  }, [imageSrc])

  useEffect(() => {
    const srcWithParams = addImageKitParams(src, { width, height, devicePixelRatio })
    setImageSrc(srcWithParams)
  }, [src, width, height, devicePixelRatio])

  if (!src) {
    return <>
      <Skeleton
        variant='rectangular'
        sx={{
          backgroundColor: 'background.default',
          ...skeletonDimensions,
          ...sx,
        }}
      />
      {props.loadedComponent}
    </>
  }

  if (!imageSrc || !isLoaded) {
    return <Skeleton
      variant='rectangular'
      sx={{
        backgroundColor: 'background.default',
        ...skeletonDimensions,
        ...sx,
      }}
    />
  }

  return <>
    <Box
      component='img'
      src={imageSrc}
      onLoad={() => setIsLoaded(true)}
      sx={{
        backgroundColor: imageSrc ? 'transparent' : '#f0f0f0',
        objectFit: 'cover',
        ...dimensions,
        ...sx,
      }}
      {...rest}
    />
    {isLoaded && props.loadedComponent
      ? props.loadedComponent
      : null}
  </>
}

export default ImageView
